import React, { useState, useEffect, useRef } from 'react';
import { Get_type_code_options, Get_today_range, Get_yesterday_range, Get_this_week_range, Get_last_week_range, Get_this_month_range } from '../Comun';
import '../FilterModal.css';

interface FilterModalProps 
{
    isOpen: boolean;
    onClose: () => void;
    onApplyFilters: (filters: {
        is_root_response: boolean;
        response: string | null;
        code: string | null;
        type: number | null;
        id_tag: number | null;
        accepted: number | null;
        start_dt: string | null;
        end_dt: string | null;
    }) => void;
    onClearFilters: () => void;
    tags: { id: number; tag: string }[];
    providers: { id: number; name: string }[];
    currentFilters: {
        is_root_response: boolean;        
        response: string | null;
        code: string | null;
        type: number | null;
        id_tag: number | null;
        accepted: number | null;
        start_dt: string | null;
        end_dt: string | null;
    };
}

const FilterEscaneoEventoModal: React.FC<FilterModalProps> = ({
    isOpen,
    onClose,
    onApplyFilters,
    onClearFilters,
    tags,
    providers,
    currentFilters}) => 
{
    const [isRootResponse, setIsRootResponse] = useState<boolean>(currentFilters.is_root_response);
    const [response, setResponse] = useState<string | null>(currentFilters.response);
    const [code, setCode] = useState<string | null>(currentFilters.code);
    const [type, setType] = useState<number | null>(currentFilters.type);
    const [idTag, setIdTag] = useState<number | null>(currentFilters.id_tag);
    const [accepted, setAccepted] = useState<number | null>(currentFilters.accepted);
    const [startDT, setStartDT] = useState<string | null>(currentFilters.start_dt);
    const [endDT, setEndDT] = useState<string | null>(currentFilters.end_dt);
    const [dateRange, setDateRange] = useState<string>('--Todas las fechas--');
    const modalRef = useRef<HTMLDivElement>(null); // Ref para el contenedor del modal 

    const typeOptions = Get_type_code_options(); // Obtener opciones de tipo de código

    useEffect(() => 
    {
        if (isOpen) 
        {
            setIsRootResponse(currentFilters.is_root_response);
            setResponse(currentFilters.response);
            setCode(currentFilters.code);
            setType(currentFilters.type);
            setIdTag(currentFilters.id_tag);
            setAccepted(currentFilters.accepted);
            setStartDT(currentFilters.start_dt);
            setEndDT(currentFilters.end_dt);
    
            if (currentFilters.start_dt === null && currentFilters.end_dt === null) 
            {
                setDateRange('--Todas las fechas--');
            } 
            else if (currentFilters.start_dt && currentFilters.end_dt) 
            {
                let start_dt: string | null = null;
                let end_dt: string | null = null;

                ({ start_dt, end_dt } = Get_today_range());
                if (start_dt === currentFilters.start_dt && end_dt === currentFilters.end_dt) 
                {
                    setDateRange('Hoy');
                } 
                else 
                {
                    ({ start_dt, end_dt } = Get_yesterday_range());
                    if (start_dt === currentFilters.start_dt && end_dt === currentFilters.end_dt)
                    {
                        setDateRange('Ayer');
                    }
                    else
                    {
                        ({ start_dt, end_dt } = Get_this_week_range());
                        if (start_dt === currentFilters.start_dt && end_dt === currentFilters.end_dt)
                        {
                            setDateRange('Esta semana');
                        }
                        else
                        {
                            ({ start_dt, end_dt } = Get_last_week_range());
                            if (start_dt === currentFilters.start_dt && end_dt === currentFilters.end_dt)
                            {
                                setDateRange('Semana pasada');
                            }
                            else
                            {
                                ({ start_dt, end_dt } = Get_this_month_range());
                                if (start_dt === currentFilters.start_dt && end_dt === currentFilters.end_dt)
                                {
                                    setDateRange('Este mes');
                                }
                                else
                                {
                                    setDateRange('Personalizado');
                                }
                            }
                        }
                    }
                } 
            } 
            else 
            {
                setDateRange('Personalizado');
            }
        }
    }, [isOpen, currentFilters]);
        
    //-----------------------------------------------------------      

    useEffect(() => {
        let start_dt: string | null = null;
        let end_dt: string | null = null;
    
        switch (dateRange) {
            case '--Todas las fechas--':
                setStartDT(null);
                setEndDT(null);
                break;
            case 'Hoy':
                ({ start_dt, end_dt } = Get_today_range());
                break;
            case 'Ayer':
                ({ start_dt, end_dt } = Get_yesterday_range());
                break;
            case 'Esta semana':
                ({ start_dt, end_dt } = Get_this_week_range());
                break;
            case 'Semana pasada':
                ({ start_dt, end_dt } = Get_last_week_range());
                break;
            case 'Este mes':
                ({ start_dt, end_dt } = Get_this_month_range());
                break;
            case 'Personalizado':
                start_dt = currentFilters.start_dt;
                end_dt = currentFilters.end_dt;
                break;
            default:
                setStartDT(null);
                setEndDT(null);
        }
    
        if (start_dt !== null && end_dt !== null) 
        {
            setStartDT(start_dt);
            setEndDT(end_dt);
        }
    }, [dateRange, currentFilters.start_dt, currentFilters.end_dt]);

    //-----------------------------------------------------------

    useEffect(() => 
    {
        // Función para manejar clics fuera del componente
        const handleClickOutside = (event: MouseEvent) => 
        {
            if (modalRef.current && !modalRef.current.contains(event.target as Node))
                onClose();    
        };
    
        // Función para manejar presiones de teclas
        const handleEscPress = (event: KeyboardEvent) => 
        {
            if (event.key === 'Escape') 
                onClose();
        };
    
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscPress);
    
        return () => 
        {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscPress);
        };
    }, [onClose]);  

    //-----------------------------------------------------------

    const handleApplyFilters = () => 
    {
        onApplyFilters({
            is_root_response : isRootResponse,
            response,
            code,
            type,
            id_tag: idTag,
            accepted,
            start_dt: startDT,
            end_dt: endDT
        });
        onClose(); // Cerrar el modal después de aplicar los filtros
    };

    //-----------------------------------------------------------

    const handleClearFilters = () => 
    {
        setIsRootResponse(false);
        setResponse(null);
        setCode(null);
        setType(null);
        setIdTag(null);
        setAccepted(null);
        setStartDT(null);
        setEndDT(null);
        setDateRange('--Todas las fechas--');
    };

    //-----------------------------------------------------------

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content" ref={modalRef}>
                <h2>Filtrar Escaneos</h2>
                <form>
                    <div className="form-group">
                        <input
                            type="text"
                            id="code"
                            value={code || ''}
                            placeholder="--Todos los códigos--"
                            onChange={(e) => setCode(e.target.value || null)}
                        />
                    </div>
                    <div className="form-group">
                        <select
                            id="rootResponse"

                            value={isRootResponse ? 'startsWith' : 'contains'}
                            onChange={(e) => setIsRootResponse(e.target.value === 'startsWith')}
                        >
                            <option value="startsWith">Empieza con</option>
                            <option value="contains">Contiene</option>
                        </select>
                        <input
                            type="text"
                            id="response"
                            className="input-response"
                            value={response || ''}
                            placeholder="--Todas las respuestas--"
                            onChange={(e) => setResponse(e.target.value || null)}
                        />
                    </div>                   
                    <div className="form-group">
                        <select
                            id="type"
                            value={type !== null ? type : ''}
                            onChange={(e) => setType(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">--Todos los tipos--</option>
                            {typeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <select
                            id="idTag"
                            value={idTag !== null ? idTag : ''}
                            onChange={(e) => setIdTag(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">--Todas las etiquetas--</option>
                            {tags.map(tag => (
                                <option key={tag.id} value={tag.id}>
                                    {tag.tag}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <select
                            id="accepted"
                            value={accepted !== null ? accepted : ''}
                            onChange={(e) => setAccepted(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">--Todos los estados--</option>
                            <option value="0" className="red">ROJO</option>
                            <option value="1" className="green">VERDE</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <select
                            id="dateRange"
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                        >
                            <option value="--Todas las fechas--">--Todas las fechas--</option>
                            <option value="Hoy">Hoy</option>
                            <option value="Ayer">Ayer</option>
                            <option value="Esta semana">Esta semana</option>
                            <option value="Semana pasada">Semana pasada</option>
                            <option value="Este mes">Este mes</option>
                            <option value="Personalizado">Personalizado</option>
                        </select>
                    </div>
                    <div className={`form-group date-range-container ${dateRange !== 'Personalizado' ? 'hidden' : ''}`}>
                        <input
                            type="datetime-local"
                            id="start_dt"
                            value={startDT || ''}
                            placeholder='Fecha desde'
                            onChange={(e) => setStartDT(e.target.value || null)}
                        />
                        <input
                            type="datetime-local"
                            id="end_dt"
                            value={endDT || ''}
                            placeholder='Fecha hasta'
                            onChange={(e) => setEndDT(e.target.value || null)}
                            className="end-date-input"
                        />
                    </div>
                    <div className="modal-actions">
                        <div className="modal-actions-right">
                            <button type="button" className="filter-button2" onClick={handleApplyFilters}>Aplicar</button>
                            <button type="button" className="filter-button" onClick={onClose}>Cancelar</button>                            
                        </div>                        
                        <button type="button" className="delete-filter-button" onClick={handleClearFilters}>
                            Borrar Filtros
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FilterEscaneoEventoModal;
